import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import size from 'lodash/size';

import checkComment from '../../utils/survey/checkComment';
import { actions as upcomingSurveyActions } from '../../redux/surveys/upcoming-survey';
import { getFlatQuestionsWithPosition } from '../Survey/helpers/getFlatQuestionsWithPosition';

const MAX_PROGRESS_VALUE = 100;

export const useDynamicQuestionsTree = ({
  config,
  answers,
  onSubmit,
  questions,
  setProgress,
  selectedValue,
}) => {
  const dispatch = useDispatch();

  const [viewQuestion, setViewQuestion] = useState();
  const [nextViewQuestion, setNextViewQuestion] = useState();
  const [answeredQuestions, setAnsweredQuestions] = useState([]);

  const flatQuestions = getFlatQuestionsWithPosition(questions, false).map(
    vQ => {
      const previousValue = answers.find(q => q.questionId === vQ.id);
      return {
        ...vQ,
        answerValue: previousValue ? previousValue.value : '',
      };
    },
  );

  useEffect(() => {
    if (viewQuestion) {
      getTargetViewQuestion({
        target: viewQuestion,
        value: selectedValue,
        assumptionLastStep: true,
        currentId: viewQuestion.id,
        handleAction: setNextViewQuestion,
      });
    }
  }, [selectedValue, viewQuestion]);

  useEffect(() => {
    const predefinedFlatQuestions = flatQuestions
      .filter(q => !!q.answerValue)
      .map(q => q.id);

    setAnsweredQuestions(predefinedFlatQuestions);

    if (!isEmpty(predefinedFlatQuestions)) {
      const targetView = flatQuestions.find(
        q =>
          q.id === predefinedFlatQuestions[predefinedFlatQuestions.length - 1],
      );
      getTargetViewQuestion({
        target: targetView,
        predefinedFlatQuestions,
        currentId: targetView.id,
        assumptionLastStep: false,
        value: targetView.answerValue,
        handleAction: setViewQuestion,
      });
    } else {
      setViewQuestion(flatQuestions[0]);
    }
  }, []);

  function handleParentChildren({
    target,
    currentId,
    handleAction,
    assumptionLastStep,
    predefinedFlatQuestions,
  }) {
    const targetParentChild = flatQuestions.find(
      q => q.id === target.parentQuestionId,
    );
    if (targetParentChild) {
      const { value: targetParentValue } =
        answers.find(q => q.questionId === targetParentChild.id) || {};
      getTargetViewQuestion({
        currentId,
        handleAction,
        assumptionLastStep,
        predefinedFlatQuestions,
        value: targetParentValue,
        target: targetParentChild,
      });
    } else {
      const rootIndex = questions.findIndex(q => q.id === target.id);
      const safeNextRoot = questions[rootIndex + 1];
      if (safeNextRoot) {
        const predefinedNextRoot = flatQuestions.find(
          ({ id }) => id === safeNextRoot.id,
        );
        handleAction(predefinedNextRoot);
      } else if (assumptionLastStep) {
        setNextViewQuestion(null);
      } else {
        dispatch(
          upcomingSurveyActions.finish({
            ...config,
            withAnswer: selectedValue,
          }),
        );
      }
    }
  }

  const getTargetViewQuestion = ({
    value,
    target,
    currentId,
    handleAction,
    assumptionLastStep,
    predefinedFlatQuestions = [],
  }) => {
    if (!isEmpty(target.questions)) {
      const targetView = target.questions.find(
        q =>
          q.triggeredOnParentValue === value &&
          ![...answeredQuestions, ...predefinedFlatQuestions].includes(q.id) &&
          currentId !== q.id,
      );
      if (targetView) {
        const predefinedQuestion = flatQuestions.find(
          ({ id }) => id === targetView.id,
        );
        handleAction(predefinedQuestion);
      } else {
        handleParentChildren({
          target,
          currentId,
          handleAction,
          assumptionLastStep,
          predefinedFlatQuestions,
        });
      }
    } else {
      handleParentChildren({
        target,
        currentId,
        handleAction,
        assumptionLastStep,
        predefinedFlatQuestions,
      });
    }
  };

  const handleSubmit = useCallback(
    ({ id, value }) => {
      setAnsweredQuestions([...answeredQuestions, viewQuestion.id]);
      getTargetViewQuestion({
        target: viewQuestion,
        value: selectedValue,
        assumptionLastStep: false,
        currentId: viewQuestion.id,
        handleAction: setViewQuestion,
      });
      onSubmit({ id, value });
    },
    [viewQuestion, answers, selectedValue],
  );

  const handleBack = useCallback(() => {
    const targetView = flatQuestions.find(
      q => q.id === answeredQuestions[answeredQuestions.length - 1],
    );
    answeredQuestions.pop();
    setViewQuestion(targetView);
  }, [viewQuestion, answers, answeredQuestions]);

  const isRequired = useMemo(
    () => get(viewQuestion, 'settings.isRequired', true),
    [viewQuestion],
  );

  const isComment = useMemo(() => checkComment(viewQuestion), [viewQuestion]);

  const isLastStep = useMemo(() => !nextViewQuestion, [nextViewQuestion]);

  const progress = useMemo(() => {
    const quantityRootLevel = uniq(flatQuestions.map(q => q.indexBlock));
    const progressRootQ = Math.round((1 / size(quantityRootLevel)) * 100);
    const progressNestedQ = quantityRootLevel.map(c => ({
      [c]: Math.floor(
        progressRootQ / flatQuestions.filter(q => q.indexBlock === c).length,
      ),
    }));
    if (viewQuestion) {
      const { acc: overallProgress } = [
        ...answeredQuestions,
        viewQuestion.id,
      ].reduce(
        ({ acc, previousIndexBlock }, viewId) => {
          const { indexBlock, parentQuestionId } =
            flatQuestions.find(q => q.id === viewId) || {};
          const targetCost =
            indexBlock &&
            progressNestedQ.find(i =>
              Object.keys(i).includes(indexBlock.toString()),
            )[indexBlock];
          if (parentQuestionId) {
            return { acc: acc + targetCost, previousIndexBlock: indexBlock };
          }

          if (
            (isLastStep && isRequired && selectedValue) ||
            (isLastStep && !isRequired) ||
            (isLastStep && isComment)
          ) {
            return {
              acc: 100,
              previousIndexBlock: indexBlock,
            };
          }

          return {
            acc:
              indexBlock !== previousIndexBlock
                ? (indexBlock - 1) * progressRootQ + targetCost
                : acc + targetCost,
            previousIndexBlock: indexBlock,
          };
        },
        { acc: 0, previousIndexBlock: 1 },
      );
      return overallProgress;
    }
    return 0;
  }, [
    answeredQuestions,
    viewQuestion,
    isLastStep,
    isRequired,
    isComment,
    selectedValue,
  ]);

  useEffect(() => {
    if (progress <= MAX_PROGRESS_VALUE) {
      setProgress(progress);
    }
  }, [progress]);

  return {
    isComment,
    handleBack,
    isRequired,
    isLastStep,
    handleSubmit,
    isFirstStep: isEmpty(answeredQuestions),
    question: {
      ...viewQuestion,
    },
  };
};



// WEBPACK FOOTER //
// ./src/components/SequentialSurvey/useDynamicQuestionsTree.jsx